import React, { useState, useRef } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import './monthDatePicker.css';

// Define Props Interface
interface MonthDatePickerProps {
  name: string;
  value?: Date | null;
  onChange: (name: string, value: Date | null) => void;
  isClearable?: boolean;
  dateFormat?: string;
}

const formatDate = (date: Date | null, format: string): string => {
  if (!date) return '';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: format.includes('MMM') ? 'short' : '2-digit',
  }).format(date);
};

// Utility functions
const isValidDate = (date: Date): boolean => date instanceof Date && !isNaN(date.getTime());

const parseDate = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return isValidDate(parsed) ? parsed : null;
};

const MonthDatePickerField: React.FC<MonthDatePickerProps> = ({
  name,
  value,
  onChange,
  isClearable = false,
  dateFormat = 'yyyy-MM',
}) => {
  const selectedDate = parseDate(value);
  const [baseYear, setBaseYear] = useState<number>(selectedDate?.getFullYear() || new Date().getFullYear());
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMonthClick = (year: number, month: number) => {
    const newDate = new Date(year, month, 1);
    if (isValidDate(newDate)) {
      onChange(name, newDate);
      setIsOpen(false); // Close picker after selection
    }
  };

  const handleClear = () => {
    onChange(name, null);
    setIsOpen(false); // Close picker after clearing
  };

  const togglePicker = () => {
    setIsOpen(prev => !prev);
  };

  // Close picker when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="month-year-picker">
      {/* Clickable Input Field */}
      <div className="month-year-picker-container">
        <input
          type="text"
          className="month-year-picker-input"
          value={selectedDate ? formatDate(selectedDate, dateFormat) : ''}
          placeholder="Select Month & Year"
          readOnly
          onClick={togglePicker}
        />
        {isClearable && selectedDate && (
          <span className="clear-btn" onClick={handleClear}>
            ✖
          </span>
        )}
      </div>

      {/* Picker Popup */}
      {isOpen && (
        <div className="react-datepicker">
          <div
            className="react-datepicker__header react-datepicker-year-header"
            style={{ justifyContent: 'space-between', display: 'flex', fontSize: '13px' }}>
            <button
              style={{ paddingLeft: 10 }}
              type="button"
              role="button"
              onClick={() => setBaseYear(prev => prev - 4)}
              aria-label="Previous 4 Years">
              {'<'}
            </button>
            <div className="react-datepicker__current-month" style={{ fontSize: '13px' }}>
              {baseYear - 3} - {baseYear}
            </div>
            <button
              type="button"
              style={{ paddingRight: 10 }}
              role="button"
              onClick={() => setBaseYear(prev => prev + 4)}
              aria-label="Previous 4 Years">
              {'>'}
            </button>
          </div>

          <div className="react-datepicker__month-container">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '10px' }}>
              {Array.from({ length: 2 }).map((_, row) => (
                <div key={row} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {Array.from({ length: 2 }).map((_, col) => {
                    const year = baseYear - 3 + row * 2 + col;
                    return (
                      <div key={year} className="react-datepicker__year">
                        <div className="react-datepicker__current-month">{year}</div>
                        <div
                          className="react-datepicker__month-wrapper"
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gridTemplateRows: 'repeat(4, auto)',
                            gap: '5px',
                          }}>
                          {Array.from({ length: 12 }).map((_, month) => (
                            <div
                              key={month}
                              className={`react-datepicker__month-text ${
                                selectedDate?.getFullYear() === year && selectedDate?.getMonth() === month
                                  ? 'react-datepicker__month--selected'
                                  : ''
                              }`}
                              onClick={() => handleMonthClick(year, month)}>
                              {new Date(year, month).toLocaleString('default', { month: 'short' })}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthDatePickerField;
